<script lang="ts">
	import { Editor } from '@tiptap/core';
	import TextAlign from '@tiptap/extension-text-align';
	import { TextStyleKit } from '@tiptap/extension-text-style';
	import StarterKit from '@tiptap/starter-kit';
	import { onDestroy, onMount } from 'svelte';
	import { handleStoreError } from '../../stores/GeneralStore';
	import Icon from '../Icon.svelte';
	import ImageExtension, { handleImageIconClicked } from './tiptap/ImageExtenstion';

	interface Props {
		noneRefresherId?: number | null | undefined;
		selected?: boolean;
		value?: string;
		bindAbleValue?: string | null;
		lastElement?: boolean;
		singleElement?: boolean;
		//Per default we use the position design because it was built for the position table.
		positionDesgin?: boolean;
		editor?: Editor | undefined;
		propertyName: string;
		callFunction?: ((object: any) => Promise<any>) | null;
		wrappInOptionalParameter?: boolean;
		allowImages?: boolean; //Should the upload icon be shown?
		disabled?: boolean;
		useCase?: "PRE_TEXT" | "PAGE" | "POSITIONS"; //Where is this styled text used?
	}

	let {
		noneRefresherId = null,
		selected = false,
		value = '',
		bindAbleValue = $bindable(),
		lastElement = false,
		singleElement = false,
		positionDesgin = true,
		editor = $bindable(),
		propertyName,
		callFunction = null,
		wrappInOptionalParameter = false,
		allowImages = true,
		disabled = false,
		useCase = "POSITIONS"
	}: Props = $props();

	let element: HTMLDivElement | undefined = $state();
	let textFocused = $state(false);
	let updateDelay: NodeJS.Timeout | null = null;
	let updateDelayMs = 1000;
	let lastNoneRefresherId: number | null | undefined = noneRefresherId;

	//First set make sure they match.
	bindAbleValue = value;

	/**
	 * In cases that you have to swap the bound item out, you have to bind the component and call this function after the bound item
	 * has changed. Otherwise, the component does not recognize the new value.
	 * @param newValue
	 */
	export function update(newValue: string, noneRefresherId?: number | null) {
		if (!editor || (lastNoneRefresherId != null && lastNoneRefresherId == noneRefresherId)) return;

		//Get the old selection
		const { from, to } = editor.state.selection;
		//editor.commands.clearContent();
		editor.commands.setContent(newValue);

		//Reaply the selection
		editor.commands.setTextSelection({ from, to });

		//Set the value after the update
		bindAbleValue = newValue;

		//Remember the last none refresher id
		lastNoneRefresherId = noneRefresherId;

		//Clear the old update timer
		if (updateDelay) {
			clearTimeout(updateDelay);
		}
	}

	function updateDisabled(disabled: boolean) {
		if (editor) {
			editor.setEditable(!disabled, false);
		}
	}

	function handleEditorUpdate(value: string) {
		if (callFunction) {
			//Clear the old update timer
			if (updateDelay) {
				clearTimeout(updateDelay);
			}

			//Now update the value.
			updateDelay = setTimeout(async () => {
				const dynamicObject: any = {};
				//Check if we should wrapp the property in an optional
				if (wrappInOptionalParameter) {
					dynamicObject[propertyName] = { value: value };
				} else {
					dynamicObject[propertyName] = value;
				}

				try {
					await callFunction!(dynamicObject);
				} catch (error) {
					//Check if we have an error type
					handleStoreError('general.patch_failed', error);

					//Reset the bindable value as well
					bindAbleValue = value;
				} finally {
					updateDelay = null;
				}
			}, updateDelayMs);
		}
	}

	onMount(() => {
		editor = new Editor({
			element: element,
			editable: !disabled,
			extensions: [
				StarterKit.configure({
					heading: {
						levels: [6]
					},
					link: false,
					code: false,
				}),
				TextStyleKit.configure({
					backgroundColor: false,
					fontFamily: false,
					fontSize: false,
					lineHeight: false,
				}),
				TextAlign.configure({
					types: ['heading', 'paragraph']
				}),
				ImageExtension.configure({
					useCase: useCase
				})
			],
			editorProps: {
				attributes: {
					class: 'h-full p-2 focus:outline-none' + (lastElement ? ' switcher' : '')
				}
			},
			content: value,
			onTransaction: () => {
				// force re-render so `editor.isActive` works as expected
				//editor = editor;
			},
			// triggered on every change
			onUpdate: ({ editor }) => {
				let html = editor.getHTML();
				//early set for fast update
				bindAbleValue = html;
				// send the content to an API here
				handleEditorUpdate(editor.getHTML());
			},
			onSelectionUpdate: () => {
				let targetColor = editor!.getAttributes('textStyle').color;
				//Ignore color updates if it's disabled
				if (disabled) return;
				iconColor = targetColor ? targetColor : '#000000';
				isBold = editor?.isActive('bold');
				isItalic = editor?.isActive('italic');
				isUnderline = editor?.isActive('underline');
				isHeading = editor?.isActive('heading', {
					level: 6
				});
				isBulledList = editor?.isActive('bulletList');
				isOrderedList = editor?.isActive('orderedList');
				isLeftAligned = editor?.isActive({
					textAlign: 'left'
				});
				isCenterAligned = editor?.isActive({
					textAlign: 'center'
				});
				isRightAligned = editor?.isActive({
					textAlign: 'right'
				});
				isParagraph = editor?.isActive('paragraph');
			}
		});

		//Add event listeners for focusing
		element?.addEventListener('focusin', () => (textFocused = true));
		element?.addEventListener('focusout', () => (textFocused = false));

		if (selected) {
			editor.chain().focus('end').run();
		}
	});

	function handleColorInputEvent(event: Event) {
		const target = event.target as HTMLInputElement;
		iconColor = target.value;
		editor?.chain().focus().setColor(target.value).run();
	}

	onDestroy(() => {
		if (editor) {
			//Check if we have a pending update
			if (updateDelay) {
				//Remove the pending update
				clearTimeout(updateDelay);
				updateDelay = null;

				//Build the update object.
				const dynamicObject: any = {};
				if (wrappInOptionalParameter) {
					dynamicObject[propertyName] = { value: editor.getHTML() };
				} else {
					dynamicObject[propertyName] = editor.getHTML();
				}

				//Call the external function imidiate for an update.
				if (callFunction) {
					callFunction(dynamicObject).catch((error) => {
						handleStoreError('general.patch_failed', error);
					});
				}
			}

			//Clean up the editor
			editor.destroy();
		}
	});

	let iconColor = $derived(disabled ? '#e3e3e3' : '#000000');
	let isBold = $state(editor?.isActive('bold'));
	let isItalic = $state(editor?.isActive('italic'));
	let isUnderline = $state(editor?.isActive('underline'));
	let isHeading = $state(
		editor?.isActive('heading', {
			level: 6
		})
	);
	let isBulledList = $state(editor?.isActive('bulletList'));
	let isOrderedList = $state(editor?.isActive('orderedList'));
	let isLeftAligned = $state(
		editor?.isActive({
			textAlign: 'left'
		})
	);
	let isCenterAligned = $state(
		editor?.isActive({
			textAlign: 'center'
		})
	);
	let isRightAligned = $state(
		editor?.isActive({
			textAlign: 'right'
		})
	);
	let isParagraph = $state(editor?.isActive('paragraph'));

	//Reactive value for updating the value in the editor.
	$effect.pre(() => {
		update(value, noneRefresherId);
		updateDisabled(disabled);
	});
</script>

<div
	class:disabled
	class="wrapper h-full flex flex-col border-x-2
  {!positionDesgin
		? 'bg-white dark:bg-dark-200 border-gray dark:fill-white dark:border-dark-200 border-2 rounded'
		: ''}
  {positionDesgin && singleElement ? 'editor-wrapper-single' : ''}
  {positionDesgin && !singleElement ? 'editor-wrapper' : ''} 
  {textFocused ? 'focus-style' : ''}
    "
>
	{#if editor}
		<div class="flex gap-6 p-2 flex-wrap">
			<div class="flex gap-2">
				<!--Bold-->
				<button
					{disabled}
					class="style-button"
					tabindex="-1"
					class:active={isBold}
					onclick={() => {
						editor?.chain().focus().toggleBold().run();
					}}
				>
					<Icon name="bold" classInfo="cursor-pointer" />
				</button>
				<!--Italic-->
				<button
					{disabled}
					class="style-button"
					tabindex="-1"
					class:active={isItalic}
					onclick={() => {
						editor?.chain().focus().toggleItalic().run();
					}}
				>
					<Icon name="italic" classInfo="cursor-pointer" />
				</button>
				<!--Underline-->
				<button
					{disabled}
					class="style-button"
					tabindex="-1"
					class:active={isUnderline}
					onclick={() => {
						editor?.chain().focus().toggleUnderline().run();
					}}
				>
					<Icon name="underline" classInfo="cursor-pointer" />
				</button>
				<!--Heading-->
				<button
					{disabled}
					class="style-button"
					tabindex="-1"
					class:active={isHeading}
					onclick={() => {
						editor?.commands.toggleHeading({ level: 6 });
					}}
				>
					<Icon name="text-size" classInfo="cursor-pointer" /></button
				>
			</div>
			<div class="flex gap-2">
				<!--Unordered list-->
				<button
					disabled={disabled || !isParagraph}
					class="style-button"
					tabindex="-1"
					class:active={isBulledList}
					onclick={() => {
						editor?.chain().focus().toggleBulletList().run();
					}}
				>
					<Icon name="list" classInfo="cursor-pointer" /></button
				>
				<!--Order list-->
				<button
					disabled={disabled || !isParagraph}
					class="style-button"
					class:active={isOrderedList}
					tabindex="-1"
					onclick={() => {
						editor?.chain().focus().toggleOrderedList().run();
					}}
				>
					<Icon name="list-ol" classInfo="cursor-pointer" /></button
				>
			</div>

			<div class="flex gap-2">
				<!--Text align left-->
				<button
					disabled={disabled || (!isParagraph && !isHeading)}
					tabindex="-1"
					class="style-button"
					class:active={isLeftAligned}
					onclick={() => {
						editor?.chain().focus().setTextAlign('left').run();
					}}
				>
					<Icon name="align-left" classInfo="cursor-pointer" /></button
				>

				<!--Text align center-->
				<button
					disabled={disabled || isBulledList || isOrderedList || (!isParagraph && !isHeading)}
					tabindex="-1"
					class="style-button"
					class:active={isCenterAligned}
					onclick={() => {
						editor?.chain().focus().setTextAlign('center').run();
					}}
				>
					<Icon name="align-center" classInfo="cursor-pointer" /></button
				>

				<!--Text align right-->
				<button
					disabled={disabled || isBulledList || isOrderedList || (!isParagraph && !isHeading)}
					tabindex="-1"
					class="style-button"
					class:active={isRightAligned}
					onclick={() => {
						editor?.chain().focus().setTextAlign('right').run();
					}}
				>
					<Icon name="align-right" classInfo="cursor-pointer" /></button
				>
			</div>

			<div class="flex gap-2">
				<!--Image-->
				{#if allowImages}
					<button
						disabled={disabled || isBulledList || isOrderedList}
						tabindex="-1"
						class="style-button"
						onclick={() => {
							if (editor) {
								handleImageIconClicked(editor, useCase);
							}
						}}
					>
						<Icon name="image" classInfo="cursor-pointer" /></button
					>
				{/if}
				<!--Color-->
				<div
					class="relative"
					class:img-button-disabled={!isParagraph && !isHeading}
					style="fill: {iconColor};"
				>
					<Icon name="palette" />
					<input
						disabled={disabled || (!isParagraph && !isHeading)}
						type="color"
						tabindex="-1"
						class:cursor-pointer={!disabled}
						style="width: 20px; height: 20px;"
						class="opacity-0 absolute top-0 left-0"
						oninput={handleColorInputEvent}
					/>
				</div>
			</div>
		</div>
	{/if}
	<div class="grow" bind:this={element}></div>
</div>

<!--svelte-ignore css_unused_selector-->
<style>
	.focus-style :global {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
}

.focus-style :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
}

	.editor-wrapper {
		border-color: transparent #e3e3e3 transparent transparent;
	}

	.editor-wrapper-single {
		border-color: transparent #e3e3e3 transparent #e3e3e3;
	}

	.wrapper:hover :global {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
}

	.wrapper:hover :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
}

	.wrapper.disabled :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1 !important;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1)) !important;
}

	.wrapper.disabled :global {
		background-color: #efefef4d;
	}

	.style-button :global:where(.dark, .dark *) {
		fill: #fdfdfd;
}

	.style-button:disabled :global {
		fill: #e3e3e3;
}

	.style-button:disabled :global:where(.dark, .dark *) {
		fill: #4e4e4e;
}

	.style-button.active :global {
		fill: #1789c0;
}

	.style-button.active :global:where(.dark, .dark *) {
		fill: #92bcdc;
}

	.img-button-disabled {
		fill: #e3e3e3 !important;
	}

	div :global(.content-img) {
		border: 2px solid #e3e3e3;
	}
	:global(div.ProseMirror-selectednode .content-img) {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
}
	:global(div.ProseMirror-selectednode .content-img):where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
}

	div :global(img:hover) {
		border-color: #2ddaff;
	}

	div :global(img:focus) {
		border-color: #2ddaff;
	}

	:global(.content-img) {
		display: block;
		float: none;
	}

	:global(.icon-button) {
		width: 36px;
		height: 36px;
		border-radius: 9999px;
		border-width: 2px;
		border-style: solid;
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(253 253 253 / var(--tw-bg-opacity, 1));
		fill: #1789c0;
		padding: 0.5rem;
	}

	:global(.icon-button):where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		background-color: transparent;
		fill: #92bcdc;
}

	:global(.icon-button):where(.dark, .dark *):hover {
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	:global(.icon-button):hover:where(.dark, .dark *) {
		fill: #fdfdfd;
}

	:global(.icon-button:disabled) {
		--tw-border-opacity: 1;
		border-color: rgb(227 227 227 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(227 227 227 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
}

	:global(.icon-button:disabled):where(.dark, .dark *) {
		fill: #7d7d7d;
}

	:global(.small-controller-box) {
		transform: translateX(100%);
	}

	:global(.slider) {
		accent-color: #1789c0;
	}
</style>
